import * as signalR from "@microsoft/signalr";
import { createContext, useContext, useEffect, useRef, useState } from "react";

export type ExportCompletedResponse = {
    jobId: string;
    downloadUrl: string;
    success: boolean;
    url: string;
}

export type ExportFailedResponse = {
  jobId: string;
  error: string;
}

export type StartExportRequest = {
  connectionId: string;
  filterData?: string;
}

export interface SignalRContextValue {
    connection: signalR.HubConnection | null;
    connectionStatus: SignalRStatusConnection;
    onExportCompleted: (callback: (data: ExportCompletedResponse) => void) => () => void;
    onExportFailed: (callback: (data: ExportFailedResponse) => void) => () => void;
    getConnectionId: () => (string | null);
}

export type SignalRStatusConnection = "connecting" | "connected" | "disconnected";

const SignalRContext = createContext<SignalRContextValue | undefined>(undefined);

export function SignalRProvider({ children }: { children: React.ReactNode }) {
    const [connectionStatus, setConnectionStatus] = useState<SignalRStatusConnection>("connecting");
    const connectionRef = useRef<signalR.HubConnection | null>(null);

    const exportCompletedCallbacks = useRef<Set<(data: ExportCompletedResponse) => void>>(new Set());
    const exportFailedCallbacks = useRef<Set<(data: ExportFailedResponse) => void>>(new Set());

    const onExportCompleted = (callback: (data: ExportCompletedResponse) => void) => {
        exportCompletedCallbacks.current.add(callback);
        return () => { exportCompletedCallbacks.current.delete(callback) };
    };

    const onExportFailed = (callback: (data: ExportFailedResponse) => void) => {
        exportFailedCallbacks.current.add(callback);
        return () => { exportFailedCallbacks.current.delete(callback) };
    };

    const getConnectionId = () => connectionRef.current?.connectionId ?? null;

    useEffect(() => {
        const connect = async () => {
        try {

            const connection = new signalR.HubConnectionBuilder()
            .withUrl('/exportHub')
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

            connection.on('ExportCompleted', (data: ExportCompletedResponse) => {
                console.log('Export completed:', data);

                exportCompletedCallbacks.current.forEach(cb => cb(data));
            });

            connection.on('ExportFailed', (data: ExportFailedResponse) => {
                console.error('Export failed:', data);
                exportFailedCallbacks.current.forEach(cb => cb(data));
            });

            await connection.start();

            console.log('SignalR connected, connectionId:', connection.connectionId);

            setConnectionStatus('connected');

            connectionRef.current = connection;

        } catch (err) {
            console.error('SignalR connection failed:', err);
            setConnectionStatus('disconnected');
        }
        };

        connect();

        return () => {
            if (connectionRef.current) {
                connectionRef.current.stop();
            }
        };
    }, []);

    return (
        <SignalRContext.Provider value={{
            connection: connectionRef.current,
            connectionStatus,
            onExportCompleted,
            onExportFailed,
            getConnectionId,
        }}>
            {children}
        </SignalRContext.Provider>
    );
}


export const useSignalR = () => {
    const context = useContext(SignalRContext);

    if (!context) throw new Error("useSignalR debe ser usado dentro del componente SignalRProvider.");

    return context
}
